import { useIntl, defineMessages } from 'react-intl';
import { Icon } from '@redturtle/iovisit-theme/components';
import { checkAndNormalizeUrl } from '@plone/volto/helpers/Url/Url';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';

const messages = defineMessages({
  email_aria_label: {
    id: 'email_aria_label',
    defaultMessage: 'Email',
  },
  website_aria_label: {
    id: 'website_aria_label',
    defaultMessage: 'Sito web',
  },
  phone_aria_label: {
    id: 'phone_aria_label',
    defaultMessage: 'Telefono',
  },
});

const ContactsItem = ({ contacts }) => {
  const intl = useIntl();
  return (
    <ul className="contacts-list">
      {contacts.map((contact) => {
        // isMail/isUrl on the raw value are unreliable (isMail only matches
        // an already-normalised "mailto:" string), so classify the contact
        // the same way UniversalLink itself does under the hood.
        const { isMail, isTelephone, isValid } = checkAndNormalizeUrl(
          contact.value,
        );
        const categoryLabel = isMail
          ? intl.formatMessage(messages.email_aria_label)
          : isTelephone
            ? intl.formatMessage(messages.phone_aria_label)
            : intl.formatMessage(messages.website_aria_label);
        // aria-label (not title, which AT doesn't reliably read) always
        // states the category and the real destination, so it stays
        // meaningful even when the visible text is an editorial label
        // (contact.type) instead of the raw value.
        const ariaLabel = `${
          contact.type ? `${contact.type} - ` : ''
        }${categoryLabel}: ${contact.value}`;
        // When the value is a valid mail/phone/url, the editorial label
        // (if any) alone is enough since the link already points to the
        // right destination. Otherwise keep the raw value visible and show
        // the label as extra context after it.
        const isLinkable = isValid;
        const label = isLinkable ? contact.type ?? contact.value : null;

        return (
          <li key={contact['@id']}>
            {label && !isTelephone ? (
              <UniversalLink href={contact.value} aria-label={ariaLabel}>
                {label}
              </UniversalLink>
            ) : (
              <>
                <UniversalLink href={contact.value} aria-label={ariaLabel}>
                  {contact.value}
                </UniversalLink>
                {!!contact.type && <> - {contact.type}</>}
              </>
            )}
            {contact.description && (
              <div className="link-description">{contact.description}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

// render old contact fields and new
const ContactInfo = ({ content }) => {
  const intl = useIntl();
  // old fields
  if (
    content.phone?.length > 0 ||
    !!content.fax ||
    !!content.email ||
    content.external_link
  )
    return (
      <>
        {content.phone?.length > 0 && (
          <>
            Tel.{' '}
            {content.phone.map((p, i) => (
              <span key={i}>
                {i > 0 && <br />}
                <a href={`tel:${p}`}>{p}</a>
              </span>
            ))}
            <br />
          </>
        )}
        {!!content.fax && (
          <>
            Fax <a href={`fax:${content.fax}`}>{content.fax}</a>
            <br />
          </>
        )}
        {content.email?.length > 0 && (
          <>
            {intl.formatMessage(messages.email_aria_label)}{' '}
            <a href={`mailto:${content.email}`}>{content.email}</a>
            <br />
          </>
        )}
        {content.external_link && (
          <p>
            <a href={content.external_link} target="_blank" rel="noreferrer">
              <Icon prefix="fas" icon="external-link-alt" className="icon" />{' '}
              {content.link_title ?? content.external_link}
            </a>
          </p>
        )}
      </>
    );

  // new object list fields
  if (content.phone_contacts?.length > 0 || content.link_contacts?.length > 0) {
    return (
      <div>
        {content.phone_contacts?.length > 0 && (
          <ContactsItem contacts={content.phone_contacts} />
        )}
        {content.link_contacts?.length > 0 && (
          <ContactsItem contacts={content.link_contacts} />
        )}
      </div>
    );
  }
};

export default ContactInfo;
